// src/pages/Contact.js
import React, { useState } from "react";
import "../index.css";
import useSEO from "../seo/useSEO";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import ReviewsCTA from "../components/ReviewsCTA";
import { trackEvent } from "../analytics/ga";

export default function Contact() {
  useSEO({
  title: "Contact photographe à Nancy & Tomblaine | HLBK",
  description:
    "Contactez HLBK, photographe à Nancy et Tomblaine pour mariages, portraits, événements et projets professionnels. Réponse sous 24h.",
  canonical: "https://hlbk.fr/contact",
  image: "https://hlbk.fr/images/og-default.jpg",
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "HLBK Photo",
      url: "https://hlbk.fr/",
      image: "https://hlbk.fr/images/og-default.jpg",
      telephone: "+33663252202",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Tomblaine",
        postalCode: "54510",
        addressCountry: "FR",
      },
      areaServed: ["FR", "BE", "LU"],
      sameAs: [
        "https://www.instagram.com/hlbkphoto",
        "https://www.facebook.com/profile.php?id=61565497088426"
      ],
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+33663252202",
        contactType: "customer service",
        areaServed: ["FR", "BE", "LU"],
        availableLanguage: ["fr", "en"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "HLBK",
      url: "https://hlbk.fr/",
    },
  ],
});

  const [form, setForm] = useState({
    nom: "",
    email: "",
    tel: "",
    sujet: "Demande de devis",
    message: "",
  });
  const [copied, setCopied] = useState(null);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setCopied("error");
      setTimeout(() => setCopied(null), 1500);
    }
  };

  const buildMailto = () => {
    const to = "contact@hlbk.fr";
    const subject = `${form.sujet} – ${form.nom || "Contact site"}`;
    const body = [
      `Bonjour Maxime,`,
      ``,
      `Nom : ${form.nom || ""}`,
      `Email : ${form.email || ""}`,
      `Téléphone : ${form.tel || ""}`,
      ``,
      `Sujet : ${form.sujet}`,
      ``,
      `${form.message || ""}`,
      ``,
      `--`,
      `Envoyé depuis le site hlbk.fr`,
    ].join("\n");

    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section className="contact-page contactx">
      {/* HERO */}
      <header className="contact-hero contactx-hero">
        <h1>Parlons de votre projet ✨</h1>
        <p>Réponse sous 24h • Déplacements France / Belgique / Luxembourg</p>

        <div className="contact-quick contactx-quick">
          <a
            href="mailto:contact@hlbk.fr"
            className="btn-solid contactx-pill"
            aria-label="Envoyer un email"
            onClick={() => trackEvent({ action: "contact_email", category: "contact", label: "hero_btn" })}
          >
            📧 contact@hlbk.fr
          </a>

          <a
            href="tel:+33663252202"
            className="btn-solid contactx-pill"
            aria-label="Appeler HLBK"
            onClick={() => trackEvent({ action: "contact_phone", category: "contact", label: "hero_btn" })}
          >
            📞 +33 6 63 25 22 02
          </a>
        </div>
      </header>

      {/* GRID */}
      <div className="contactx-grid">
        {/* FORM */}
        <div className="contact-card contactx-card">
          <h2>Écrivez-moi</h2>

          <form
            className="contact-form"
            onSubmit={(e) => {
              e.preventDefault();
              window.location.href = buildMailto();
            }}
          >
            <div className="contactx-form-grid">
              <div className="field">
                <label htmlFor="nom">Nom *</label>
                <input
                  id="nom"
                  name="nom"
                  type="text"
                  required
                  value={form.nom}
                  onChange={update}
                  placeholder="Votre nom / société"
                />
              </div>

              <div className="field">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update}
                  placeholder="vous@exemple.com"
                />
              </div>

              <div className="field">
                <label htmlFor="tel">Téléphone</label>
                <input
                  id="tel"
                  name="tel"
                  type="tel"
                  value={form.tel}
                  onChange={update}
                  placeholder="+33 ..."
                />
              </div>

              <div className="field">
                <label htmlFor="sujet">Sujet *</label>
                <select id="sujet" name="sujet" value={form.sujet} onChange={update}>
                  <option>Demande de devis</option>
                  <option>Informations</option>
                  <option>Collaboration</option>
                  <option>Autre</option>
                </select>
              </div>
            </div>

            <div className="field">
              <label htmlFor="message">Votre message *</label>
              <textarea
                id="message"
                name="message"
                required
                value={form.message}
                onChange={update}
                placeholder="Type (portrait, mariage, événement, corporate), lieu, date, attentes…"
              />
            </div>

            <div className="actions contactx-actions">
              <button type="submit" className="btn-solid contactx-primary">
                Envoyer
              </button>
              <a href="tel:+33663252202" className="btn-ghost contactx-secondary">
                Appeler
              </a>
            </div>

            <p className="contactx-hint">
              En envoyant ce message, vous acceptez d’être recontacté par email ou téléphone pour votre demande.
            </p>
          </form>
        </div>

        {/* INFOS */}
        <aside className="contact-card contactx-card">
          <h2>Coordonnées</h2>

          <div className="contact-rows">
            <div className="row">
              <span className="row-label">Email</span>
              <div className="row-val">
                <a href="mailto:contact@hlbk.fr">contact@hlbk.fr</a>
                <button className="mini" onClick={() => copy("contact@hlbk.fr", "email")} aria-label="Copier l'email">
                  {copied === "email" ? "Copié" : "Copier"}
                </button>
              </div>
            </div>

            <div className="row">
              <span className="row-label">Téléphone</span>
              <div className="row-val">
                <a href="tel:+33663252202">+33 6 63 25 22 02</a>
                <button className="mini" onClick={() => copy("+33663252202", "tel")} aria-label="Copier le téléphone">
                  {copied === "tel" ? "Copié" : "Copier"}
                </button>
              </div>
            </div>

            <div className="row">
              <span className="row-label">Localisation</span>
              <div className="row-val">Tomblaine (54510), France</div>
            </div>
          </div>

          <div className="badges" aria-label="Zones de service et infos">
            <span className="badge" title="France">🇫🇷</span>
            <span className="badge" title="Belgique">🇧🇪</span>
            <span className="badge" title="Luxembourg">🇱🇺</span>
            <span className="badge">Réponse &lt; 24h</span>
            <span className="badge">Déplacements</span>
          </div>

          <div className="infos-pratiques contactx-infos">
            <h3>Infos pratiques</h3>
            <ul>
              <li>📄 Devis sous 24h.</li>
              <li>🚗 Déplacements France, Belgique, Luxembourg (sur devis).</li>
              <li>🖼️ Fichiers HD optimisés web & print, galerie de sélection incluse.</li>
            </ul>

            <div className="contactx-socials">
              <a
                href="https://www.facebook.com/profile.php?id=61565497088426"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                title="Facebook"
              >
                <FaFacebookF size={20} />
              </a>
              <a
                href="https://www.instagram.com/hlbkphoto"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                title="Instagram"
              >
                <FaInstagram size={20} />
              </a>
            </div>

            <div className="contactx-reviews">
              <ReviewsCTA
                href="https://g.page/r/CWoruq03bw4oEAE/review"
                text="Laisser un avis sur Google"
              />
            </div>
          </div>
        </aside>

        {/* MAP full width */}
        <div className="contact-card contactx-card contactx-map">
          <iframe
            title="Carte Tomblaine"
            src="https://www.google.com/maps?q=Tomblaine%2054510&output=embed"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="contact-map-iframe"
          />
        </div>
      </div>
    </section>
  );
}